import React from 'react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import { useTheme } from 'emotion-theming';
import ResizeObserver from 'react-resize-observer';
import DataTable from 'react-data-table-component';

import Avatar from '../../../shared/_TBComponents/Avatar/Avatar';
import { buildTeambuildrStyles } from '../../../shared/components/Table/Table';
import Text from '../../../shared/_TBComponents/Text';
import {
  TABLE_MARGIN_TOP,
  NARROW_BREAKPOINT,
  MINIMIZED_BREAKPOINT,
  CHANGE_DIRECTION_BETTER,
} from '../boardConstants';
import { getChangeArrow } from '../helpers';

const BASE_FONT_SIZE = 16; // 1em reference for the scale cascade below

// AtomicText hardcodes its own font-size, overriding any inherited scaled one
// — see BEHAVIOR.md Invariants for why every custom-rendered cell needs its
// own explicit fontSize.
const CELL_TEXT_FONT_SIZE = 16;

const RANK_COLUMN_WIDTH = 65;
const VALUE_COLUMN_MIN_WIDTH = 150; // shared by the Result and Change columns
const AVATAR_SIZE = 35;
const CHANGE_ARROW_UP_FONT_SIZE = 12;
const CHANGE_ARROW_DOWN_FONT_SIZE = 10;

const TableWrapper = styled('div')`
    padding: 0 1.25em;
  .rdt_TableBody {
    padding: 0 0.3125em;
  }
`;

// These render inside a table cell with its own scaled 12px font-size (set
// via Table.js's customStyles) — a closer ancestor than TableWrapper's 16px,
// so their em values are relative to 12px, not 16px.
const NameCell = styled('div')`
  display: flex;
  align-items: center;

  p {
    margin-left: ${(props) => (props.boardWidth <= NARROW_BREAKPOINT ? '0px' : '1.25em')};
  }
`;

const ChangeCell = styled('div')`
  display: flex;
  align-items: center;
  gap: 0.417em;
`;

const NoDataComponent = () => null;

const getColumns = (boardWidth, scaleFactor, theme, leaderboardForm) => {
  const cellFontSize = Math.round(CELL_TEXT_FONT_SIZE * scaleFactor);

  const cols = [
    {
      name: 'Rank',
      selector: 'rank',
      width: `${Math.round(RANK_COLUMN_WIDTH * scaleFactor)}px`,
      center: true,
      cell: (row) => (
        <Text styledTextProps={{ fontWeight: 900, fontSize: cellFontSize }}>
          {row.rank}
        </Text>
      ),
    },
    {
      name: 'Name',
      selector: 'userName',
      style: {
        fontWeight: '700',
      },
      cell: (row) => (
        <NameCell boardWidth={boardWidth}>
          {boardWidth > NARROW_BREAKPOINT && (
            <Avatar
              avatar={row.userPic}
              name={row.userName}
              size={Math.round(AVATAR_SIZE * scaleFactor)}
            />
          )}
          <Text styledTextProps={{ fontSize: cellFontSize }}>{row.userName}</Text>
        </NameCell>
      ),
    },
    {
      name: 'Date',
      selector: 'dateSet',
      omit: boardWidth <= NARROW_BREAKPOINT,
      center: true,
      style: {
        color: '#444444',
        fontWeight: 400,
      },
    },
    {
      name: 'Result',
      selector: 'displayValue',
      minWidth: `${Math.round(VALUE_COLUMN_MIN_WIDTH * scaleFactor)}px`,
      grow: 0,
      center: true,
      cell: (row) => (
        <Text styledTextProps={{ fontWeight: 600, fontSize: cellFontSize }}>
          {row.displayValue}
        </Text>
      ),
    },
    {
      name: 'Change',
      selector: 'changeDisplayValue',
      minWidth: `${Math.round(VALUE_COLUMN_MIN_WIDTH * scaleFactor)}px`,
      grow: 0,
      omit: leaderboardForm.compareTo === 0 || boardWidth <= MINIMIZED_BREAKPOINT,
      center: true,
      cell: (row) => {
        // changeRawValue is the API's compare-to value, not the difference.
        const changeArrow = getChangeArrow(row.rawValue, row.changeRawValue);
        return (
          <ChangeCell>
            <Text
              styledTextProps={{
                fontWeight: 600,
                fontSize: Math.round((changeArrow === '▲ '
                  ? CHANGE_ARROW_UP_FONT_SIZE
                  : CHANGE_ARROW_DOWN_FONT_SIZE) * scaleFactor),
                color: row.changeDirection === CHANGE_DIRECTION_BETTER ? theme.colors.green : 'red',
              }}
            >
              {changeArrow}
            </Text>
            <Text styledTextProps={{ fontWeight: 600, fontSize: cellFontSize }}>
              {row.changeDisplayValue === null || row.changeDisplayValue === '' || row.changeDisplayValue === 0 ? '-' : row.changeDisplayValue}
            </Text>
          </ChangeCell>
        );
      },
    },
  ];

  return cols;
};

const LeaderboardTable = ({
  boardWidth,
  handleLoadMore,
  leaderboardData,
  leaderboardForm,
  scaleFactor,
  tableEnd,
  tableRef,
  setTableHeight,
  setCurrentScroll,
}) => {
  const theme = useTheme();

  const columns = React.useMemo(
    () => getColumns(boardWidth, scaleFactor, theme, leaderboardForm),
    [boardWidth, scaleFactor, theme, leaderboardForm],
  );

  const loadMore = () => {
    handleLoadMore();
  };

  return (
    <TableWrapper
      ref={tableRef}
      style={{
        fontSize: `${BASE_FONT_SIZE * scaleFactor}px`,
        marginTop: `${Math.round(TABLE_MARGIN_TOP * scaleFactor)}px`,
      }}
    >
      {/*
        Instead of using a scroll event listener which can be expensive, we're using
        the resize observer api which determines changes to the dimensions and position information
        of an element. By placing it within the TableWrapper we are observing the leaderboard table
        and can use the rect.top data for scroll location.
      */}
      <ResizeObserver
        onResize={(rect) => {
          setTableHeight(rect.height);
        }}
        onPosition={(rect) => {
          if (rect.top <= tableEnd) {
            loadMore();
            setCurrentScroll(rect.top);
          }
        }}
      />
      <DataTable
        center
        columns={columns}
        data={leaderboardData}
        highlightOnHover
        noHeader
        noDataComponent={<NoDataComponent />}
        customStyles={buildTeambuildrStyles(scaleFactor)}
        theme='teambuildr'
      />
    </TableWrapper>
  );
};

LeaderboardTable.propTypes = {
  boardWidth: PropTypes.number.isRequired,
  handleLoadMore: PropTypes.func.isRequired,
  leaderboardData: PropTypes.instanceOf(Array).isRequired,
  leaderboardForm: PropTypes.instanceOf(Object).isRequired,
  scaleFactor: PropTypes.number.isRequired,
  tableEnd: PropTypes.number.isRequired,
  tableRef: PropTypes.instanceOf(Object).isRequired,
  setTableHeight: PropTypes.func.isRequired,
  setCurrentScroll: PropTypes.func.isRequired,
};

export default React.memo(LeaderboardTable);
